import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Ic_pdf from "../../assets/images/icons/pdf.png";
import Ic_jpeg from "../../assets/images/icons/jpg.svg";
import ClaudAdd from "../../assets/images/icons/cloud-add.svg";
import RemoveIcon from "../../assets/images/icons/remove-red-icon.svg";
import DoneIcon from "../../assets/images/icons/Check-green-icon.svg";
import { FILETYPES, MAX_FILE_SIZE } from "../../Constants/fileConstants";

interface FileUploadProps {
  onFileChange: (file: File) => void;
  file: File | null;
  onCancel: () => void;
  uploadSuccess: boolean;
  errorMessageKey?: string;
  label?: string;
  defaultImage?: string;
  accept?: string;
  className?: string;
  disabled?: boolean;
  name?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileChange,
  file,
  onCancel,
  uploadSuccess,
  errorMessageKey,
  defaultImage,
  label,
  accept,
  className,
  disabled,
  name,
}) => {
  const { t } = useTranslation();
  const [internalError, setInternalError] = useState<string | null>(null);

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case "application/pdf":
        return Ic_pdf;
      case "image/jpeg":
      case "image/png":
        return Ic_jpeg;
      default:
        return Ic_jpeg;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const acceptedTypes = (accept ?? FILETYPES)
        .split(",")
        .map((type) => type.trim().replace(".", "").toLowerCase());

      const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

      if (!fileExtension || !acceptedTypes.includes(fileExtension)) {
        setInternalError(
          t("file_should_be_one_of") +
            " " +
            acceptedTypes.join(", ").toUpperCase(),
        );
        onCancel();
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        setInternalError(
          t("file_exceeds_size_limit", { max: MAX_FILE_SIZE / (1024 * 1024) }),
        );
        onCancel();
        return;
      }

      setInternalError(null);
      onFileChange(selectedFile);
    }
  };

  const handleCancel = () => {
    setInternalError(null);
    onCancel();
  };

  const fileName = file
    ? file?.name.length > 20
      ? file.name?.slice(0, 20) + file.name?.slice(-4, file.name?.length)
      : file.name
    : "";
  const size: number = file ? file?.size : 0;

  return (
    <div
      className={`mb-20 w-full text-center ${className} ${!uploadSuccess && "rounded-lg border border-dashed"}`}
    >
      {file || defaultImage ? (
        <div className="w-full text-center">
          <div className="flex w-full items-center justify-center space-x-4">
            <img
              src={file ? getFileIcon(file.type) : defaultImage}
              alt="File Icon"
              className={
                defaultImage ? "h-32 w-32 rounded-lg object-cover" : "h-10 w-8"
              }
            />
            <div className="flex w-full flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold">
                  {`${fileName} (${Math.round(size / 1024)} KB)`}{" "}
                </p>
                <img
                  src={RemoveIcon}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center"
                  onClick={handleCancel}
                  alt="Remove"
                />
              </div>
              {/* {defaultImage || uploadSuccess ? (
                <p className="h-1.5 w-full rounded-lg bg-successGreen"></p>
              ) : (
                <p className="bg-error h-2 w-full rounded-lg"></p>
              )} */}
              <div className="flex items-center gap-2">
                {(defaultImage || uploadSuccess) && (
                  <img src={DoneIcon} className="h-3 w-3" alt="Done" />
                )}
                {uploadSuccess && (
                  <p className="text-xs font-bold text-positive">
                    {t("uploaded")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <label className="mb-4 w-full cursor-pointer text-center">
          <input
            type="file"
            className="hidden"
            accept={accept ?? FILETYPES}
            onChange={handleFileChange}
            disabled={disabled}
            name={name}
          />
          <div className="border-bg-systemBg flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-4">
            <img src={ClaudAdd} alt="Add File" />
            <p className="text-primary-text">
              {label || t("upload_files_here")}
            </p>
            <p className="text-sm text-body-text">
              {accept ? accept.replaceAll(".", "").toUpperCase() : FILETYPES}
            </p>
          </div>
        </label>
      )}
      {errorMessageKey && (
        <p className="w-full text-center text-error">{t(errorMessageKey)}</p>
      )}
      {internalError && (
        <p className="w-full text-center text-error">{internalError}</p>
      )}
    </div>
  );
};

export default FileUpload;
